import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Ban, AlertTriangle } from 'lucide-react';
import { AdminUser } from '../../services/userManagementService';

interface BanAccountModalProps {
  user: AdminUser | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (userId: string, reason: string) => void;
  isLoading: boolean;
}

const BanAccountModal: React.FC<BanAccountModalProps> = ({
  user,
  isOpen,
  onClose,
  onConfirm,
  isLoading
}) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');

  const handleConfirm = () => {
    if (!user || !reason.trim()) return;
    onConfirm(user.id, reason);
    setReason('');
  };

  const handleClose = () => {
    setReason('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <Ban className="h-5 w-5" />
            {t('adminPanel.banAccount.title')}
          </DialogTitle>
          <DialogDescription>
            {t('adminPanel.banAccount.description')} <strong>{user?.full_name}</strong> ({user?.email})
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="bg-red-50 border border-red-200 rounded-md p-3 space-y-2">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-semibold text-red-900">
                  ⚠️ {t('adminPanel.banAccount.warningTitle')}
                </p>
                <ul className="text-sm text-red-800 list-disc list-inside space-y-1">
                  <li>{t('adminPanel.banAccount.warningList.item1')}</li>
                  <li>{t('adminPanel.banAccount.warningList.item2')}</li>
                  <li>{t('adminPanel.banAccount.warningList.item3')}</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">{t('adminPanel.banAccount.reasonLabel')}</Label>
            <Textarea
              id="reason"
              placeholder={t('adminPanel.banAccount.reasonPlaceholder')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={5}
              className="resize-none"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!reason.trim() || isLoading}
            variant="destructive"
          >
            {isLoading ? t('adminPanel.banAccount.banning') : t('adminPanel.banAccount.banButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BanAccountModal;
